import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { getPaymentMethodIcon, type IconName } from '@sg/garnish';

/**
 * Converts a payment type into a label and an icon.
 */
export const useLabelAndIconByType = (): ((
  type: 'card' | 'apple_pay' | 'google_pay' | string,
) => PaymentTypeConversion) => {
  const { formatMessage } = useIntl();

  return useCallback(
    (type: string) => {
      const icon = getPaymentMethodIcon(type);

      switch (type) {
        case 'card': {
          return {
            label: '',
            icon,
          };
        }

        case 'apple_pay': {
          return {
            label: formatMessage(messages.applePay),
            icon,
          };
        }

        case 'google_pay': {
          return {
            label: formatMessage(messages.googlePay),
            icon,
          };
        }

        case 'cashapp': {
          return {
            label: formatMessage(messages.cashApp),
            icon,
          };
        }

        case 'link': {
          return {
            label: formatMessage(messages.link),
            icon,
          };
        }

        default: {
          return {
            label: formatMessage(messages.paymentMethod),
            icon,
          };
        }
      }
    },
    [formatMessage],
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  applePay: {
    defaultMessage: 'Apple Pay',
    description: 'Stripe Payment | Apple Pay',
  },
  googlePay: {
    defaultMessage: 'Google Pay',
    description: 'Stripe Payment | Google Pay',
  },
  cashApp: {
    defaultMessage: 'Cash App',
    description: 'Stripe Payment | Cash App',
  },
  link: {
    defaultMessage: 'Link',
    description: 'Stripe Payment | Link',
  },
  paymentMethod: {
    defaultMessage: 'Payment Method',
    description: 'Stripe Payment | Payment Method',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type PaymentTypeConversion = {
  label: string;
  icon: IconName;
};
